import React, { useCallback, useMemo, useState } from "react";
import { AuthManagerContext } from "../contexts/AuthManager";
import {
    BalanceType,
    formatActivityTypeTitle,
    formatCurrency,
    formatDateForGraphql,
    getContractPercentage,
} from "../utils";
import {
    ActivityType,
    Contract,
    ContractState,
    FeeType,
    Mission,
    MissionCount,
    MissionDeduction,
    MissionFee,
    ProfileType,
    Repetition,
    RepetitionType,
    TerminateReason,
} from "../__generated__/graphql";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { DELETE_MISSION, TERMINATE_MISSION } from "../graphql/core";
import { LabelMode } from "../ui_atoms/Label";
import { useDurationShortFormatter, useI18n } from "./i18n-hooks";
import { addDays } from "date-fns";
import { FIND_MY_MISSIONS } from "../graphql/home";
import { IconName, hasIcon } from "../components/Icon";
import { useContractSigningInfo } from "./contacts";
import { Maybe } from "@graphql-tools/utils";
import { GET_MISSION_OVERTIME_STATUS, GET_MISSION_VACATION_STATUS } from "../graphql/timesheet";
import { SHORT_DATE, useDateFormatter, useDateRangeFormatter } from "./date";

export const useActivityTypeFormatter = () => {
    const { user } = React.useContext(AuthManagerContext);
    const result = useCallback(
        (activityType: Pick<ActivityType, "title">) => {
            if (!user?.gender) {
                throw new Error("User's gender is not defined");
            }
            return formatActivityTypeTitle(activityType.title, user.gender);
        },
        [user],
    );
    return result;
};

export type MissionAction = "delete" | "terminate" | "close";

type MissionActionFunction = (
    action: MissionAction,
    mission: Mission,
    reason: TerminateReason,
    terminationDate?: Date,
    otherReason?: string,
) => void | Promise<void>;

type MissionHookResult = {
    onAction: MissionActionFunction;
    processingAction: MissionAction | null;
    successAction: MissionAction | null;
    error: ApolloError | null;
    reset: () => void;
};

export const useMissionHandler = (
    onActionSuccess?: (action: MissionAction, mission: Mission) => void | Promise<void>,
): MissionHookResult => {
    const [processingAction, setProcessingAction] = useState<MissionAction | null>(null);
    const [deleteMission, { error: deleteError, data: deleteData, reset: deleteReset }] =
        useMutation(DELETE_MISSION);
    const [
        terminateMission,
        { error: terminateError, data: terminateData, reset: terminateReset },
    ] = useMutation(TERMINATE_MISSION, { refetchQueries: [FIND_MY_MISSIONS] });

    const successAction = useMemo(() => {
        if (deleteData) {
            return "delete";
        }
        if (terminateData) {
            return "terminate";
        }
        return null;
    }, [deleteData, terminateData]);

    const error = deleteError || terminateError || null;
    const reset = useCallback(() => {
        setProcessingAction(null);
        deleteReset();
        terminateReset();
    }, [deleteReset, terminateReset]);

    const onAction = useCallback<MissionActionFunction>(
        async (action, mis, reason, terminationDate, otherReason) => {
            reset();
            setProcessingAction(action);
            try {
                switch (action) {
                    case "delete":
                        await deleteMission({ variables: { missionId: mis.id } });
                        break;
                    case "terminate":
                        if (!terminationDate) {
                            terminationDate = new Date();
                        }
                        await terminateMission({
                            variables: {
                                missionId: mis.id,
                                data: {
                                    reason,
                                    terminationDate: formatDateForGraphql(terminationDate),
                                    otherReason: otherReason,
                                },
                            },
                        });
                        break;
                    default:
                        break;
                }
                if (onActionSuccess) {
                    await onActionSuccess(action, mis);
                }
            } finally {
                setProcessingAction(null);
            }
        },
        [reset, onActionSuccess, deleteMission, terminateMission],
    );

    return {
        onAction,
        processingAction,
        successAction,
        error,
        reset,
    };
};

type MissionAttribute = {
    labelMode: LabelMode;
};

const STATE_MODE_MAP: Record<ContractState, MissionAttribute> = {
    accepted: { labelMode: "success" },
    cancelled: { labelMode: "error" },
    draft: { labelMode: "default" },
    ended: { labelMode: "info" },
    pause: { labelMode: "info" },
    pendingAcceptation: { labelMode: "warning" },
    terminated: { labelMode: "error" },
};

const useComputeMissionStatusDisplay = () => {
    const i18n = useI18n();
    const formatDate = useDateFormatter();
    const { getStatusText } = useContractSigningInfo();

    return useCallback(
        (contract: Contract, profileType: ProfileType) => {
            const contractState = contract.state;
            const signatureStatus = contract.signatureStatus;

            if (contractState == ContractState.Draft) {
                return i18n.t("mission.details.draft_title");
            }
            if (contractState == ContractState.Ended) {
                return i18n.t("mission.details.ended_title");
            }

            if (contractState == ContractState.Terminated) {
                return i18n.t("mission.details.terminated_title");
            }
            if (contractState == ContractState.Cancelled) {
                return i18n.t("mission.details.cancelled_title");
            }
            if (contractState == ContractState.Accepted) {
                if (
                    contract.startDate &&
                    new Date(contract.startDate) > addDays(new Date(), -7) &&
                    new Date(contract.startDate) > new Date()
                ) {
                    return i18n.t("mission.details.will_start_at", {
                        date: formatDate(new Date(contract.startDate), SHORT_DATE),
                    });
                }
                return i18n.t("mission.details.accepted_title");
            }
            if (contractState == ContractState.PendingAcceptation) {
                return getStatusText(profileType, signatureStatus);
            }
            return i18n.t("contract.signing_status.unknown");
        },
        [formatDate, getStatusText, i18n],
    );
};

const useComputeContractSigningStatusDisplay = () => {
    const i18n = useI18n();
    const { getStatusText } = useContractSigningInfo();
    return useCallback(
        (contract: Contract, profileType: ProfileType) => {
            const contractState = contract.state;
            const signatureStatus = contract.signatureStatus;

            if (contractState == ContractState.Draft) {
                return i18n.t("mission.details.draft_title");
            }
            if (contractState == ContractState.Ended) {
                return i18n.t("mission.details.ended_title");
            }

            if (contractState == ContractState.Terminated) {
                return i18n.t("mission.details.terminated_title");
            }
            if (contractState == ContractState.Cancelled) {
                return i18n.t("mission.details.cancelled_title");
            }
            if (contractState == ContractState.Accepted) {
                return i18n.t("contract.signing_status.signed");
            }
            if (contractState == ContractState.PendingAcceptation) {
                return getStatusText(profileType, signatureStatus);
            }
            return i18n.t("contract.signing_status.unknown");
        },
        [getStatusText, i18n],
    );
};

export const useMissionDisplayAttributes = () => {
    const displayContractAttribute = useContractDisplayAttributes();

    return useCallback(
        (mission?: Mission | null, profileType?: ProfileType | null) => {
            const contractAttributes = displayContractAttribute(
                mission?.currentContract,
                profileType,
            );
            return { ...contractAttributes };
        },
        [displayContractAttribute],
    );
};

const FEE_ICONS: Record<FeeType, IconName> = {
    [FeeType.Cct]: "cct",
    [FeeType.Other]: "fee-other",
    [FeeType.Food]: "fee-food",
    [FeeType.Phone]: "fee-phone",
    [FeeType.RemoteWork]: "fee-remote-work",
    [FeeType.PrivateTransport]: "fee-private-transportation",
    [FeeType.PublicTransport]: "fee-public-transportation",
};

export const useContractDisplayAttributes = () => {
    const intDateFormatter = useDateFormatter();
    const i18n = useI18n();

    const formatDate = useCallback(
        (d?: string | null, empty = "") => {
            if (!d) {
                return empty;
            }
            return intDateFormatter(new Date(d), {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
        [intDateFormatter],
    );
    const formatDateRange = useDateRangeFormatter();
    const computeMissionStatusDisplay = useComputeMissionStatusDisplay();
    const computeContractStatusDisplay = useComputeContractSigningStatusDisplay();

    return useCallback(
        (contract?: Contract | null, profileType?: ProfileType | null) => {
            const buildRepetitionKey = (repetition: RepetitionType) => {
                if (
                    contract?.workingTimeClassicSpec?.repetition === Repetition.None &&
                    repetition === RepetitionType.WorkedMonth
                ) {
                    return i18n.t("fees.repetition.total");
                }
                return i18n.t(`fees.repetition.${repetition}`);
            };

            if (contract && profileType) {
                const startAt = formatDate(contract.startDate);
                const effectiveEndAt = formatDate(contract.effectiveEndDate);
                const duration =
                    contract.startDate && contract.endDate
                        ? formatDateRange(
                              new Date(contract.startDate),
                              new Date(contract.endDate),
                              "long",
                          )
                        : i18n.t("mission.duration_without_end", { start: startAt });
                const terminationDate = effectiveEndAt
                    ? i18n.t("mission.terminationDate", { date: effectiveEndAt })
                    : undefined;
                let salaryGross = "";
                let salaryInvoice = "";
                if (contract.grossSalary && contract.invoiceSalary) {
                    salaryGross = i18n.t(`mission.salary_${contract.earningType}`, {
                        value: formatCurrency({ amountInCents: contract.grossSalary }),
                    });
                    salaryInvoice = i18n.t(`mission.salary_${contract.earningType}`, {
                        value: formatCurrency({ amountInCents: contract.invoiceSalary }),
                    });
                }
                const workPercentage = getContractPercentage(contract) || "";
                const canCancel =
                    [ContractState.Accepted, ContractState.PendingAcceptation].includes(
                        contract.state,
                    ) &&
                    // is the contract has an end date , it must be in the future
                    // this should never occur as the scheduler backend will
                    // end the mission automatically
                    (contract.endDate ? new Date(contract.endDate) > new Date() : true);
                return {
                    ...STATE_MODE_MAP[contract.state || "draft"],
                    durationInfo: duration,
                    terminationDate: terminationDate,
                    salaryGross,
                    salaryInvoice,
                    workPercentage: workPercentage ? `${workPercentage}%` : "",
                    missionStatus: computeMissionStatusDisplay(contract, profileType),
                    contractStatus: computeContractStatusDisplay(contract, profileType),
                    fees: (contract.fees || []).map((value) => ({
                        key: `${value.type}-${value.externalId}`,
                        icon: hasIcon(value.externalId || "")
                            ? (value.externalId as IconName)
                            : FEE_ICONS[value.type],
                        title:
                            value.type === FeeType.Other
                                ? i18n.t("fees.type.otherText.title", {
                                      text: value.otherTitle || "???",
                                  })
                                : value.type === FeeType.Cct
                                  ? i18n.t(`fees.cct.${value.externalId}_title`)
                                  : i18n.t(`fees.type.${value.type}.title`),
                        description: `${formatCurrency({
                            amountInCents:
                                profileType === ProfileType.Client
                                    ? value.invoiceAmount || value.amount
                                    : value.amount,
                        })} ${buildRepetitionKey(value.repetition)}`,
                    })),
                    deductions: (contract.deductions || []).map((value) => ({
                        key: `${value.description}-${value.externalId}`,
                        title: i18n.t("deduction.title", { description: value.description }),
                        description: `${formatCurrency({
                            amountInCents: value.amount,
                        })} ${buildRepetitionKey(value.repetition)}`,
                    })),
                    vacationDays: i18n.t("mission.vacationDaysValue", {
                        days: contract.vacationDays || 0,
                    }),
                    canCancel: canCancel,
                };
            } else {
                return {
                    ...STATE_MODE_MAP["draft"],
                    durationInfo: "",
                    terminationDate: "",
                    salaryGross: "",
                    salaryInvoice: "",
                    workPercentage: "",
                    vacationDays: "",
                    missionStatus: i18n.t("mission.details.draft_title"),
                    contractStatus: i18n.t("contract.signing_status.unknown"),
                    fees: [],
                    deductions: [],
                    canCancel: false,
                };
            }
        },
        [
            computeContractStatusDisplay,
            computeMissionStatusDisplay,
            formatDate,
            i18n,
            formatDateRange,
        ],
    );
};

export const useRelevantMissionCounter = () => {
    return useCallback((missionCount?: Maybe<MissionCount>) => {
        if (!missionCount) {
            return 0;
        }
        return missionCount.ended + missionCount.accepted + missionCount.terminated;
    }, []);
};

export const useMissionHolidayBalance = (missionId?: string) => {
    const skip = !missionId;
    const {
        data,
        loading,
        refetch: _refetch,
    } = useQuery(GET_MISSION_VACATION_STATUS, {
        variables: { missionId: missionId || "" },
        skip,
        fetchPolicy: "cache-and-network",
    });
    const formatDuration = useDurationShortFormatter();
    const formatDate = useDateFormatter();
    const refetch = useMemo(() => (skip ? () => {} : _refetch), [skip, _refetch]);

    return useMemo(() => {
        if (!missionId) {
            return { refetch, balance: undefined };
        }
        const balance: [boolean, BalanceType] = [loading, { text: "", date: undefined }];
        if (data) {
            const vacationAmountMinutes = data.getMissionVacationStatus.availableVacationMinutes;
            const dailyHours =
                parseFloat(data.getMissionVacationStatus.contractHoursPerWeekFullTime) / 5;
            const accountingDate = data.getMissionVacationStatus.accountingDate;
            if (accountingDate) {
                balance[1].date = formatDate(new Date(accountingDate));
            }
            if (vacationAmountMinutes > 0) {
                // use html char for plus and minus to avoid markdown transformation
                balance[1].text = `&plus; ${formatDuration(Math.abs(vacationAmountMinutes), dailyHours)}`;
            } else if (vacationAmountMinutes < 0) {
                balance[1].text = `&minus; ${formatDuration(Math.abs(vacationAmountMinutes), dailyHours)}`;
            } else {
                balance[1].text = "0";
            }
        }
        return { refetch, balance };
    }, [data, formatDate, formatDuration, loading, missionId, refetch]);
};

export const useMissionOvertimeBalance = (missionId?: string) => {
    const skip = !missionId;
    const {
        data,
        loading,
        refetch: _refetch,
    } = useQuery(GET_MISSION_OVERTIME_STATUS, {
        variables: { missionId: missionId || "" },
        skip: !missionId,
        fetchPolicy: "cache-and-network",
    });
    const formatDuration = useDurationShortFormatter();
    const formatDate = useDateFormatter();
    const refetch = useMemo(() => (skip ? () => {} : _refetch), [skip, _refetch]);
    return useMemo(() => {
        if (!missionId) {
            return { refetch, balance: undefined };
        }
        const balance: [boolean, BalanceType] = [loading, { text: "", date: undefined }];
        if (data) {
            const overtimeMinutes = data.getMissionOvertimeStatus.overtimeMinutes;
            const accountingDate = data.getMissionOvertimeStatus.accountingDate;
            if (accountingDate) {
                balance[1].date = formatDate(new Date(accountingDate));
            }
            if (overtimeMinutes > 0) {
                balance[1].text = `&plus; ${formatDuration(Math.abs(overtimeMinutes))}`;
            } else if (overtimeMinutes < 0) {
                balance[1].text = `&minus; ${formatDuration(Math.abs(overtimeMinutes))}`;
            } else {
                balance[1].text = "0";
            }
        }
        return { refetch, balance };
    }, [data, formatDate, formatDuration, loading, missionId, refetch]);
};

export const useBuildMissionDeductionDescription = () => {
    const i18n = useI18n();

    return useCallback(
        ({
            deductions,
            isPackage,
        }: {
            deductions?: Array<MissionDeduction> | null;
            isPackage: boolean;
        }): string => {
            const byRepetition = {
                [RepetitionType.Count]: [] as Array<number>,
                [RepetitionType.WorkedMonth]: 0,
                [RepetitionType.WorkedDay]: 0,
            };
            for (const deduction of deductions || []) {
                if (deduction.repetition === RepetitionType.Count) {
                    byRepetition[deduction.repetition].push(deduction.amount);
                } else {
                    byRepetition[deduction.repetition] += deduction.amount;
                }
            }
            const description = [];
            if (byRepetition[RepetitionType.Count].length > 0) {
                for (const amount of byRepetition[RepetitionType.Count]) {
                    description.push(
                        `${formatCurrency({ amountInCents: amount })} ${i18n.t("fees.repetition.count")}`,
                    );
                }
            } else if (byRepetition[RepetitionType.WorkedMonth] > 0) {
                description.push(
                    `${formatCurrency({ amountInCents: byRepetition[RepetitionType.WorkedMonth] })} ${isPackage ? i18n.t("fees.repetition.total") : i18n.t("fees.repetition.worked_month")}`,
                );
            } else if (byRepetition[RepetitionType.WorkedDay] > 0) {
                description.push(
                    `${formatCurrency({ amountInCents: byRepetition[RepetitionType.WorkedDay] })} ${i18n.t("fees.repetition.worked_day")}`,
                );
            }
            return description.join("\n");
        },
        [i18n],
    );
};

export const useBuildMissionFeeDescription = () => {
    const i18n = useI18n();

    return useCallback(
        ({
            fees,
            isPackage,
            isClient,
        }: {
            fees?: Array<MissionFee> | null;
            isPackage: boolean;
            isClient: boolean;
        }): string => {
            const byRepetition = {
                [RepetitionType.Count]: [] as Array<number>,
                [RepetitionType.WorkedMonth]: 0,
                [RepetitionType.WorkedDay]: 0,
            };
            for (const fee of fees || []) {
                const amount = isClient ? fee.invoiceAmount : fee.amount;
                if (fee.repetition === RepetitionType.Count) {
                    byRepetition[fee.repetition].push(amount);
                } else {
                    byRepetition[fee.repetition] += amount;
                }
            }
            const description = [];
            if (byRepetition[RepetitionType.Count].length > 0) {
                for (const amount of byRepetition[RepetitionType.Count]) {
                    description.push(
                        `${formatCurrency({ amountInCents: amount })} ${i18n.t("fees.repetition.count")}`,
                    );
                }
            } else if (byRepetition[RepetitionType.WorkedMonth] > 0) {
                description.push(
                    `${formatCurrency({ amountInCents: byRepetition[RepetitionType.WorkedMonth] })} ${isPackage ? i18n.t("fees.repetition.total") : i18n.t("fees.repetition.worked_month")}`,
                );
            } else if (byRepetition[RepetitionType.WorkedDay] > 0) {
                description.push(
                    `${formatCurrency({ amountInCents: byRepetition[RepetitionType.WorkedDay] })} ${i18n.t("fees.repetition.worked_day")}`,
                );
            }
            return description.join("\n");
        },
        [i18n],
    );
};
